import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {validateInvite, acceptInvite} from "./AuthService";

export default function PortalInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [status, setStatus] = useState("validating"); // validating | valid | invalid | success
  const [errorMsg, setErrorMsg] = useState("");

  // Password form state
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");

  /* ----- validate the token on mount ----- */
  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      setErrorMsg("Missing invite token.");
      return;
    }

    const runValidate = async () => {
      try {
        const ok = await validateInvite(token);
        setStatus(ok ? "valid" : "invalid");
        if (!ok) setErrorMsg("Invite is invalid or has expired.");
      } catch (err) {
        console.error(err);
        setErrorMsg((err instanceof Error ? err.message : "Error validating invite"));
        setStatus("invalid");
      }
    };

    runValidate();
  }, [token]);

  /* ----- submit new password ----- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setSubmitErr("Passwords do not match.");
    try {
      setSubmitting(true);
      await acceptInvite(token, password);
      setStatus("success");
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setSubmitErr((err instanceof Error ? err.message : "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  /* ----- helpers for rendering ----- */
  const renderInvalid = () => (
    <div className="max-w-md mx-auto p-6 mt-10 bg-white rounded-xl shadow text-center">
      <h1 className="text-xl font-semibold mb-2 text-red-600">Invite Invalid</h1>
      <p className="mb-4 text-gray-700">{errorMsg}</p>
      <button
        onClick={() => navigate("/")}
        className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded"
      >
        Go Home
      </button>
    </div>
  );

  const renderSuccess = () => (
    <div className="max-w-md mx-auto p-6 mt-10 bg-white rounded-xl shadow text-center">
      <h1 className="text-xl font-semibold text-emerald-700 mb-2">
        Account Created!
      </h1>
      <p className="text-gray-700">Redirecting you to the login page…</p>
    </div>
  );

  /* ----- Render ----- */
  if (status === "validating")
    return <p className="p-8 text-center text-gray-600">Validating invite…</p>;

  if (status === "invalid") return renderInvalid();
  if (status === "success") return renderSuccess();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-10 px-4">
      <div className="w-full max-w-md bg-white p-6 rounded-xl shadow">
        <h1 className="text-2xl font-bold text-center text-emerald-700 mb-6">
          Set Your Password
        </h1>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">New Password</label>
            <input
              type="password"
              className="w-full border px-3 py-2 rounded"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirm Password</label>
            <input
              type="password"
              className="w-full border px-3 py-2 rounded"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {submitErr && <p className="text-sm text-red-600">{submitErr}</p>}
          <button
            type="submit"
            disabled={submitting}
            className={`w-full text-white py-2 rounded font-medium ${
              submitting ? "bg-gray-400" : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {submitting ? "Submitting…" : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
}
